import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Users, SlidersHorizontal, X, Building2, Loader2, Banknote } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { EmptyState } from "@/components/common/Loading";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { CategoryBadge } from "@/components/common/CategoryBadge";
import { useAuth } from "@/context/AuthContext";
import { useLocalMode } from "@/context/LocalModeContext";
import { DirectoryFilters, DIR_FILTERS } from "@/components/directory/DirectoryFilters";
import { isRegionCenter } from "@/components/directory/GeoRadiusFilter";
import { ConnectionsStack } from "@/components/social/Connections";
import { AccountTagInline } from "@/components/tags/AccountTag";
import { DirectoryCreateButton } from "@/components/common/DirectoryCreateButton";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { trackResult } from "@/lib/analytics";
import { useAnalyticsImpressions } from "@/hooks/useAnalyticsImpressions";

const META = {
  people: { title: "People", desc: "Discover founders, operators, and mentors.", endpoint: "people" },
  businesses: { title: "Businesses", desc: "Explore startups and companies.", endpoint: "businesses", create: { label: "Add business", to: "/dashboard/businesses?new=1", testId: "businesses-create-cta" } },
  capital: { title: "Capital", desc: "Find investors, VCs, and funds.", endpoint: "capital", create: { label: "Add capital profile", to: "/dashboard/capital-profiles?new=1", testId: "capital-create-cta" } },
};
const loc = (l) => [l?.city, l?.state].filter(Boolean).join(", ");
const emptyState = () => ({ multi: {}, sort: "recent", geoOn: false, center: null, radius: 50 });

const SkeletonGrid = () => (
  <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <Card key={i} className="p-5">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/3" /></div>
        </div>
        <Skeleton className="mt-4 h-3 w-full" />
        <Skeleton className="mt-2 h-3 w-4/5" />
        <div className="mt-3 flex gap-1.5"><Skeleton className="h-5 w-16 rounded-full" /><Skeleton className="h-5 w-16 rounded-full" /></div>
      </Card>
    ))}
  </div>
);

// Exported so the Group member directory can render the SAME people card instead of
// reimplementing one (GRP-DEC-302). No behaviour or prop changed for this page.
export function ResultCard({ it, kind, references, trackRef, onTrackClick }) {
  const navigate = useNavigate();
  const to = kind === "people" ? `/p/${it.slug}` : kind === "capital" ? `/capital/${it.slug}` : `/b/${it.slug}`;
  const goBiz = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (it.primary_affiliation?.slug) navigate(`/b/${it.primary_affiliation.slug}`);
  };
  return (
    <Link to={to} ref={trackRef} onClick={onTrackClick}>
      <Card className="flex h-full flex-col border-0 p-5" data-testid={`directory-card-${it.slug}`}>
        <div className="flex items-center gap-3">
          {kind === "people" ? (
            <Avatar className="h-[3.75rem] w-[3.75rem] shrink-0">{it.headshot_url && <AvatarImage src={fileUrl(it.headshot_url)} className="object-cover" />}<AvatarFallback className="bg-teal-50 text-teal-700">{(it.display_name || "U")[0]}</AvatarFallback></Avatar>
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden bg-secondary">{it.profile_image_url ? <img src={fileUrl(it.profile_image_url)} alt="" className="h-full w-full object-cover" /> : <span className="font-[600] text-muted-foreground">{(it.display_name || "?")[0]}</span>}</div>
          )}
          <div className="min-w-0">
            <div className="font-[640] text-foreground break-words">{it.display_name}</div>
            {it.profile_category && <CategoryBadge value={it.profile_category} references={references} className="mt-1" testId={`directory-category-${it.slug}`} />}
            {it.handle && <div className="text-sm text-primary">@{it.handle}</div>}
            {kind === "people" && it.account_tags?.length > 0 && (
              <AccountTagInline tags={it.account_tags} max={1} size="xs" className="mt-0.5" testId={`directory-card-${it.slug}-tags`} />
            )}
            {it.capital_type && <div className="text-sm text-muted-foreground">{labelFor(references.capital_types, it.capital_type)}</div>}
          </div>
        </div>
        {it.headline && <p className="mt-3 line-clamp-2 text-sm text-foreground">{it.headline}</p>}
        {loc(it.location) && <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {loc(it.location)}</div>}
        {kind === "people" && it.primary_affiliation && (
          <div className="mt-1.5 flex items-center gap-1.5 text-sm text-muted-foreground" data-testid={`directory-card-${it.slug}-affiliation`}>
            <Building2 className="h-3.5 w-3.5 shrink-0" />
            {it.primary_affiliation.slug ? (
              <span role="link" tabIndex={0} onClick={goBiz} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goBiz(e)} className="truncate font-[560] text-foreground hover:text-primary hover:underline" data-testid={`directory-card-${it.slug}-affiliation-link`}>{it.primary_affiliation.display_name}</span>
            ) : (
              <span className="truncate font-[560] text-foreground">{it.primary_affiliation.display_name}</span>
            )}
          </div>
        )}
        {kind === "people" && (it.connections?.count > 0 || it.mutual_count > 0) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
            {it.connections?.count > 0 && <ConnectionsStack avatars={it.connections.avatars} count={it.connections.count} />}
            {it.mutual_count > 0 && (
              <span className="text-xs font-[600] text-primary" data-testid={`directory-mutual-${it.slug}`}>{it.connections?.count > 0 ? "· " : ""}{it.mutual_count} mutual</span>
            )}
          </div>
        )}
        {(it.industries || []).length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
            {(it.industries || []).slice(0, 3).map((i) => <Badge key={i} variant="secondary" className="bg-secondary">{labelFor(references.industries, i)}</Badge>)}
            {(it.industries || []).length > 3 && <Badge variant="secondary" className="bg-secondary" data-testid={`directory-card-${it.slug}-more-industries`}>+{(it.industries || []).length - 3}</Badge>}
          </div>
        )}
      </Card>
    </Link>
  );
}

export default function Directory({ kind = "people" }) {
  const meta = META[kind] || META.people;
  const { references } = useReferences();
  const { user } = useAuth();
  const localMode = useLocalMode();
  const isAdmin = user?.role === "admin";
  // Slow rollout: the Capital directory is covered for everyone except admins.
  const gated = kind === "capital" && !isAdmin;
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState(emptyState);
  const groups = useMemo(() => DIR_FILTERS[kind] || [], [kind]);

  // Reset when switching directory type.
  useEffect(() => { setFilters(emptyState()); setQ(""); }, [kind]);

  // Default the geo center to the signed-in user's saved location.
  useEffect(() => {
    let ok = true;
    if (user) {
      api.get("/directory/my-location").then(({ data }) => {
        if (ok && data && data.lat != null) {
          setFilters((f) => (f.center ? f : { ...f, center: { lat: data.lat, lng: data.lng, label: data.label || "My location" } }));
        }
      }).catch(() => {});
    }
    return () => { ok = false; };
  }, [user, kind]);

  const patch = useCallback((p) => setFilters((f) => ({ ...f, ...p })), []);
  const clearAll = useCallback(() => setFilters((f) => ({ multi: {}, sort: "recent", geoOn: false, center: f.center, radius: 50 })), []);

  // "Nearest" requires a center — fall back to "recent" if the center is removed.
  useEffect(() => {
    if (!filters.center && filters.sort === "nearest") setFilters((f) => ({ ...f, sort: "recent" }));
  }, [filters.center, filters.sort]);

  // Debounce the free-text query separately so filter changes fetch immediately.
  const [dq, setDq] = useState("");
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  // Serialized keys so the fetch effect re-runs reliably on any filter change.
  const multiKey = JSON.stringify(filters.multi);
  const centerKey = filters.center ? `${filters.center.lat},${filters.center.lng}` : "";

  // Build one page of results. The bottom sentinel auto-loads the next page (offset).
  const fetchPage = useCallback(async (offset, signal) => {
    if (gated) return [];
    const params = { limit: 24, offset };
    if (dq) params.q = dq;
    groups.forEach((g) => { const arr = filters.multi[g.key]; if (arr && arr.length) params[g.key] = arr.join(","); });
    let sort = filters.sort;
    if (filters.geoOn && filters.center) {
      const c = filters.center;
      if (isRegionCenter(c)) {
        if (c.granularity === "state") params.state = c.state_code;
        else params.country = c.country_code;
      } else {
        params.lat = c.lat; params.lng = c.lng; params.radius = filters.radius;
      }
    } else if (sort === "nearest") {
      sort = "recent";
    }
    params.sort = sort;
    if (localMode.localParams) Object.assign(params, localMode.localParams);
    const { data } = await api.get(`/directory/${meta.endpoint}`, { params, signal });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.endpoint, dq, multiKey, filters.sort, filters.geoOn, filters.radius, centerKey, gated, localMode.localKey]);

  const resetKey = `${meta.endpoint}|${dq}|${multiKey}|${filters.sort}|${filters.geoOn}|${filters.radius}|${centerKey}|${gated}|${localMode.localKey}`;
  const { items, loadingMore, hasMore, sentinelRef } = useInfiniteScroll({ fetchPage, resetKey, pageSize: 24 });
  const bindImpression = useAnalyticsImpressions("directory_impression");

  const chips = useMemo(() => {
    const out = [];
    groups.forEach((g) => (filters.multi[g.key] || []).forEach((v) => out.push({ key: g.key, value: v, label: labelFor(references[g.ref], v) })));
    if (filters.geoOn && filters.center) out.push({ key: "__geo", value: "geo", label: isRegionCenter(filters.center) ? `In ${filters.center.label}` : `Within ${filters.radius} mi of ${filters.center.label}` });
    return out;
  }, [filters, groups, references]);

  const removeChip = (chip) => {
    if (chip.key === "__geo") { patch({ geoOn: false }); return; }
    patch({ multi: { ...filters.multi, [chip.key]: (filters.multi[chip.key] || []).filter((v) => v !== chip.value) } });
  };

  const hasActive = chips.length > 0;
  const filterPanel = <DirectoryFilters kind={kind} value={filters} onChange={patch} onClear={clearAll} hasActive={hasActive} isAdmin={user?.role === "admin"} />;

  if (gated) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-[680] tracking-[-0.01em] text-foreground">{meta.title}</h1>
        <div className="mt-8 flex justify-center">
          <Card className="w-full max-w-2xl border-0 px-6 py-16 text-center sm:px-10" data-testid="capital-coming-soon">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 text-teal-700">
              <Banknote className="h-8 w-8" />
            </div>
            <h2 className="mt-6 text-2xl font-[700] tracking-tight text-foreground sm:text-3xl">Coming Soon!</h2>
            <p className="mx-auto mt-4 max-w-md text-base text-muted-foreground">
              When we've onboarded the majority of our investors, we'll be opening up this section to all users! Thank you
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-[680] tracking-[-0.01em] text-foreground">{meta.title}</h1>
          <p className="mt-2 text-muted-foreground">{meta.desc}</p>
        </div>
        {meta.create && <DirectoryCreateButton label={meta.create.label} to={meta.create.to} testId={meta.create.testId} />}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Desktop filter sidebar */}
        <aside className="hidden lg:col-span-3 lg:block">
          <div className="lg:sticky lg:top-20">{filterPanel}</div>
        </aside>

        {/* Results column */}
        <div className="lg:col-span-9">
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[200px] flex-1">
              <Input placeholder={`Search ${meta.title.toLowerCase()}…`} value={q} onChange={(e) => setQ(e.target.value)} data-testid={`directory-${kind}-search`} />
            </div>
            {/* Mobile filters */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="secondary" className="lg:hidden" data-testid="directory-mobile-filters"><SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters</Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[320px] overflow-y-auto">{filterPanel}</SheetContent>
            </Sheet>
            <Select value={filters.sort} onValueChange={(v) => patch({ sort: v })}>
              <SelectTrigger className="w-[170px]" data-testid="directory-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A–Z)</SelectItem>
                <SelectItem value="name_desc">Name (Z–A)</SelectItem>
                <SelectItem value="recent">Newest Added</SelectItem>
                <SelectItem value="nearest" disabled={!filters.center}>Nearest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {chips.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {chips.map((c, i) => (
                <button key={`${c.key}-${c.value}-${i}`} onClick={() => removeChip(c)} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs font-[500] text-foreground transition-colors hover:bg-secondary" data-testid={`applied-chip-${c.value}`}>
                  {c.label}<X className="h-3 w-3" />
                </button>
              ))}
              <button onClick={clearAll} className="text-xs font-[540] text-primary hover:underline">Clear all</button>
            </div>
          )}

          {items !== null && (
            <div className="mt-4 text-sm text-muted-foreground" data-testid="directory-result-count">{items.length} result{items.length !== 1 ? "s" : ""}</div>
          )}

          {items === null ? (
            <SkeletonGrid />
          ) : items.length === 0 ? (
            <div className="mt-4"><EmptyState icon={Users} title="No matches" description="Try removing a filter or widening your distance." action={<button onClick={clearAll} className="text-primary hover:underline">Clear all filters</button>} testId={`directory-${kind}-empty`} /></div>
          ) : (
            <>
              <div className="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {items.map((it) => (
                  <ResultCard
                    key={it.slug}
                    it={it}
                    kind={kind}
                    references={references}
                    trackRef={bindImpression(it.analytics)}
                    onTrackClick={it.analytics ? () => trackResult("directory_result_clicked", it.analytics) : undefined}
                  />
                ))}
              </div>
              <div ref={sentinelRef} aria-hidden="true" className="h-1" />
              {loadingMore && (
                <div className="mt-6 flex justify-center" data-testid="directory-loading-more"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              )}
              {!hasMore && items.length > 0 && (
                <div className="mt-6 text-center text-sm text-muted-foreground" data-testid="directory-end">You've reached the end · {items.length} result{items.length !== 1 ? "s" : ""}</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
